import {nextTick, reactive} from 'vue'
import {errors, Rest} from 'utils'
import router from 'router'
import {signOut} from '@aws-amplify/auth'
import $chats from 'stores/chats'
import $profile from 'stores/profile'
import $tenants from 'stores/tenants'
import $settings from 'stores/settings'
import $integrations from 'stores/integrations'
import {API_LOGOUT} from 'consts/api'
import {API2_ALL_CAPS} from 'consts/api2'
import $prompts from 'stores/prompts'
import {LOGOUT_EVENT} from 'consts/event'


import * as $utils from 'utils';
// TODO: Change to BaseStore
function defaultState() {
  const state = {
    loading: false,
    loaded: false,
    error: undefined,
    debug: true,
    nav_pinned: localStorage.getItem('nav_pinned') === 'true',
    caps: {},
  }
  return state
}

function makeState() {
  const state = reactive({
    ...defaultState()
  })
  return state
}

export const store = {
  state: makeState(),
  _rest: new Rest(),

  goToLogin() {
    return router.push({
      name: 'login'
    })
  },

  goToWelcome() {
    return router.push({
      name: 'welcome'
    })
  },

  goToBrand() {
    return router.push({
      name: 'brand'
    })
  },

  goToProfile() {
    return router.push({
      name: 'profile'
    })
  },

  goToChats() {
    return router.push({
      name: 'new-chat'
    })
  },

  goToChat(id) {
    return router.push({
      name: 'chat',
      query: {id}
    })
  },

  goToSettings(tab, params = {}) {
    return router.push({
      name: 'settings',
      query: {tab},
      ...params
    })
  },

  goToAnalyticsTenant(tab, params = {}) {
    return router.push({
      name: 'analytics-tenant',
      query: {tab},
      ...params
    })
  },

  goToOnboarding() {
    return router.push({
      name: 'onboarding'
    })
  },

  goToTenants() {
    return router.push({
      name: 'tenants'
    })
  },

  goToTenant(id) {
    return router.push({
      name: 'tenant',
      params: {id: id}
    })
  },

  goToTenantUser(tenant_id, user_id) {
    return router.push({
      name: 'tenant_user',
      params: {id: tenant_id, user_id: user_id}
    })
  },

  goToPulse(tab = 'feed'){
    return router.push({
      name: 'pulse',
      query: {tab},
    })
  },

  get nav_pinned() {
    return this.state.nav_pinned
  },

  set nav_pinned(value) {
    localStorage.setItem('nav_pinned', value)
    this.state.nav_pinned = value
  },

  async setOnboarding(data) {
    $settings.onboarding = data
    await $settings.save()
  },

  async deleteOnboarding() {
    await $settings.remove('onboarding')
    this.goToOnboarding()
  },

  setError(error) {
    if (error instanceof errors.Cancelled) {
      return console.warn(error.message)
    }

    if (error instanceof errors.Unauthorized) {
      nextTick(async () => await signOut())
      return
    }

    if (error instanceof errors.Maintenance) {
      window.location.href = window.location.origin
      window.location.reload()
      return
    }

    this.state.error = error
    console.error(error)
  },

  clearError() {
    this.state.error = undefined

    // We handle login in a special way - do not allo to reload the same url
    // It can be SAML error redirect and if we just reload user would appear on
    // the same error page. So we force to start clean login
    if (window.location.pathname == '/login') {
      window.location.href = window.location.origin
      nextTick(() => {
        window.location.reload()
      })
      return
    }

    window.location.reload()
  },

  async start() {
    this.state.loaded = false
    this.state.loading = true

    // Caps ALWAYS should be loaded first
    await this.reload_caps()
    await $settings.load()
    await $profile.load({login: true})
    await $tenants.loadCaps()
    await $chats.load()
    await $prompts.load()
    await $integrations.load()

    this.state.loading = false
    this.state.loaded = true
  },

  async logout() {
    try {
      window.dispatchEvent(new CustomEvent(LOGOUT_EVENT))
      await this._rest.get(API_LOGOUT)
    }
    catch(err) {
      console.log('API_LOGOUT', err)
    }
    await signOut()
  },

  //
  // TODO: Change getters to BaseStore?
  //
  get loading() {
    return this.state.loading
  },

  get loaded() {
    return this.state.loaded
  },

  get onboarding() {
    return $settings.onboarding
  },

  get tooltips_enabled(){
    return $settings.tooltips_enabled
  },

  get caps() {
    return this.state.caps
  },

  async reload_caps() {
    const caps = await this._rest.get(API2_ALL_CAPS)
    this.state.caps = {...caps}
  },

  async setSuCaps(caps) {
    const to_send = $utils.object.getChangedFields(this.state.caps, caps)
    await $settings.putMany(Object.entries(to_send).map(([key, value]) => ({key, value})))
    this.state.caps = {...caps, ...to_send}
  },

  async setAdminCaps(caps) {
    const to_send = $utils.object.getChangedFields(this.state.caps, caps)
    await $settings.putMany(Object.entries(to_send).map(([key, value]) => ({key, value})))
    this.state.caps = {...caps, ...to_send}
  },

  get fileUploadEnabled() {
    return $tenants.caps.sources.includes('@files')
  },

  get chatSharingEnabled() {
    return this.state.caps.adm_chat_sharing_enabled
  },

  get gCalendarEnabled() {
    return this.state.caps.su_g_calendar_enabled
  },

  get msCalendarEnabled() {
    return this.state.caps.su_ms_calendar_enabled
  },

  get salesforceEnabled() {
    return this.state.caps.su_salesforce_enabled
  },


  get pulseSemanticSearchEnabled(){
    // return true
    return this.state.caps.adm_pulse_semantic_search_enabled
  },

  get onboarding_enabled(){
    return this.state.caps.adm_onboarding_enabled
  },


  get error() {
    return this.state.error
  },

  get parent_page() {
    return window.opener
  },

  get max_style() {
    return DEV_MODE === 'true' || window.location.origin.includes('amdocs.genrate.ai')
  },

  get brand_name() {
    return this.max_style ? 'Max' : 'Genrate'
  }
}

export default store
